import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Button, Input, Select, toast } from '../components/UI'
import { Logo } from '../components/Layout'
import { Eye, EyeOff, Building2, Megaphone, ArrowRight } from 'lucide-react'

// ── Background decoration ────────────────────────────────────
function AuthBg() {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: -1,
      background: 'linear-gradient(135deg, var(--navy) 0%, #1a3a6b 50%, var(--navy) 100%)',
      overflow: 'hidden'
    }}>
      {/* Animated circles */}
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          borderRadius: '50%',
          background: `rgba(48,102,190,${0.05 + i * 0.03})`,
          width: `${200 + i * 120}px`,
          height: `${200 + i * 120}px`,
          top: `${10 + i * 15}%`,
          left: `${-10 + i * 20}%`,
          animation: `pulse ${3 + i}s ease-in-out infinite alternate`,
        }} />
      ))}
      <div style={{
        position: 'absolute', bottom: '10%', right: '5%',
        fontFamily: 'var(--font-display)', fontSize: '12rem', fontWeight: 900,
        color: 'rgba(255,255,255,0.03)', lineHeight: 1, userSelect: 'none'
      }}>📍</div>
    </div>
  )
}

// ── Login Page ───────────────────────────────────────────────
export function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '' })
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const { signIn } = useAuth()
  const navigate = useNavigate()

  function validate() {
    const e = {}
    if (!form.email) e.email = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email'
    if (!form.password) e.password = 'Password is required'
    return e
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setLoading(true)
    try {
      await signIn(form.email, form.password)
      navigate('/dashboard')
    } catch (err) {
      toast.error(err.message || 'Login failed. Check your credentials.')
      setErrors({ general: err.message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <AuthBg />
      <div style={{ width: '100%', maxWidth: 440 }} className="animate-slideUp">
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo.png" alt="AdsLocate" style={{ height: 64, width: 'auto' }} />
        </div>

        <div className="card" style={{ padding: 32 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', marginBottom: 4 }}>Welcome back</h2>
          <p style={{ color: 'var(--gray-500)', fontSize: '0.9rem', marginBottom: 24 }}>Sign in to your AdsLocate account</p>

          {errors.general && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#991B1B', fontSize: '0.88rem' }}>
              {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Input
              label="Email Address"
              type="email"
              placeholder="you@company.com"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              error={errors.email}
              required
            />
            <Input
              label="Password"
              type={showPw ? 'text' : 'password'}
              placeholder="Your password"
              value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              error={errors.password}
              required
              iconRight={
                <button type="button" onClick={() => setShowPw(!showPw)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', display: 'flex' }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />

            <div style={{ textAlign: 'right', marginTop: -8 }}>
              <Link to="/forgot-password" style={{ fontSize: '0.82rem', color: 'var(--tech)', fontWeight: 500 }}>
                Forgot password?
              </Link>
            </div>

            <Button type="submit" loading={loading} fullWidth size="lg" iconRight={<ArrowRight size={16} />}>
              Sign In
            </Button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--gray-100)', fontSize: '0.88rem', color: 'var(--gray-500)' }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: 'var(--tech)', fontWeight: 600 }}>Create account</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Register Page ─────────────────────────────────────────────
export function RegisterPage() {
  const [step, setStep] = useState(1) // 1: role, 2: details
  const [role, setRole] = useState('')
  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '',
    full_name: '', company_name: '', phone: ''
  })
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const { signUp } = useAuth()
  const navigate = useNavigate()

  function validateForm() {
    const e = {}
    if (!form.full_name) e.full_name = 'Full name is required'
    if (!form.company_name) e.company_name = 'Company name is required'
    if (!form.email) e.email = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email'
    if (!form.password) e.password = 'Password is required'
    else if (form.password.length < 8) e.password = 'Minimum 8 characters'
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match'
    return e
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const errs = validateForm()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setLoading(true)
    try {
      await signUp(form.email, form.password, role, {
        full_name: form.full_name,
        company_name: form.company_name,
        phone: form.phone,
      })
      toast.success('Account created! Please check your email to verify.')
      navigate('/dashboard')
    } catch (err) {
      toast.error(err.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  if (step === 1) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <AuthBg />
        <div style={{ width: '100%', maxWidth: 520 }} className="animate-slideUp">
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <img src="/logo.png" alt="AdsLocate" style={{ height: 64, width: 'auto' }} />
            <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: 8, fontSize: '0.9rem' }}>The OOH Advertising Marketplace</p>
          </div>

          <div className="card" style={{ padding: 32 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', marginBottom: 4 }}>Join AdsLocate</h2>
            <p style={{ color: 'var(--gray-500)', fontSize: '0.9rem', marginBottom: 28 }}>How will you be using AdsLocate?</p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              {/* Advertiser */}
              <div
                onClick={() => setRole('advertiser')}
                style={{
                  border: `2px solid ${role === 'advertiser' ? 'var(--tech)' : 'var(--gray-200)'}`,
                  borderRadius: 'var(--radius-lg)',
                  padding: 24,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: role === 'advertiser' ? 'rgba(48,102,190,0.05)' : 'white',
                  textAlign: 'center'
                }}
              >
                <div style={{
                  width: 56, height: 56, borderRadius: 'var(--radius)',
                  background: role === 'advertiser' ? 'var(--tech)' : 'var(--gray-100)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 12px', color: role === 'advertiser' ? 'white' : 'var(--gray-400)',
                  transition: 'all 0.2s'
                }}>
                  <Megaphone size={24} />
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 4 }}>I'm an Advertiser</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', lineHeight: 1.4 }}>Looking for ad spaces to promote my brand</div>
              </div>

              {/* Media Owner */}
              <div
                onClick={() => setRole('media_owner')}
                style={{
                  border: `2px solid ${role === 'media_owner' ? 'var(--tech)' : 'var(--gray-200)'}`,
                  borderRadius: 'var(--radius-lg)',
                  padding: 24,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: role === 'media_owner' ? 'rgba(48,102,190,0.05)' : 'white',
                  textAlign: 'center'
                }}
              >
                <div style={{
                  width: 56, height: 56, borderRadius: 'var(--radius)',
                  background: role === 'media_owner' ? 'var(--tech)' : 'var(--gray-100)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 12px', color: role === 'media_owner' ? 'white' : 'var(--gray-400)',
                  transition: 'all 0.2s'
                }}>
                  <Building2 size={24} />
                </div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 4 }}>I'm a Media Owner</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', lineHeight: 1.4 }}>I own ad spaces and want to monetize them</div>
              </div>
            </div>

            <Button
              fullWidth size="lg"
              disabled={!role}
              onClick={() => setStep(2)}
              iconRight={<ArrowRight size={16} />}
            >
              Continue as {role === 'advertiser' ? 'Advertiser' : role === 'media_owner' ? 'Media Owner' : '...'}
            </Button>

            <div style={{ textAlign: 'center', marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--gray-100)', fontSize: '0.88rem', color: 'var(--gray-500)' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: 'var(--tech)', fontWeight: 600 }}>Sign in</Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <AuthBg />
      <div style={{ width: '100%', maxWidth: 520 }} className="animate-slideUp">
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo.png" alt="AdsLocate" style={{ height: 64, width: 'auto' }} />
        </div>

        <div className="card" style={{ padding: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: '1.2rem' }}>←</button>
            <div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem' }}>Create your account</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>
                Signing up as {role === 'advertiser' ? '🎯 Advertiser' : '📢 Media Owner'}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="grid-2">
              <Input label="Full Name" placeholder="Ahmad Al Mansouri" value={form.full_name}
                onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} error={errors.full_name} required />
              <Input label="Company Name" placeholder="Company LLC" value={form.company_name}
                onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} error={errors.company_name} required />
            </div>
            <Input label="Email Address" type="email" placeholder="you@company.com" value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))} error={errors.email} required />
            <Input label="Phone Number" type="tel" placeholder="+971 50 000 0000" value={form.phone}
              onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
            <Input label="Password" type={showPw ? 'text' : 'password'} placeholder="Min 8 characters" value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))} error={errors.password} required
              hint="Minimum 8 characters"
              iconRight={<button type="button" onClick={() => setShowPw(!showPw)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', display: 'flex' }}>{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>} />
            <Input label="Confirm Password" type="password" placeholder="Repeat password" value={form.confirmPassword}
              onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))} error={errors.confirmPassword} required />

            <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', lineHeight: 1.5 }}>
              By creating an account you agree to our <a href="/terms" style={{ color: 'var(--tech)' }}>Terms of Service</a> and <a href="/privacy" style={{ color: 'var(--tech)' }}>Privacy Policy</a>. AdsLocate charges a 10% commission on completed bookings.
            </p>

            <Button type="submit" loading={loading} fullWidth size="lg" iconRight={<ArrowRight size={16} />}>
              Create Account
            </Button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--gray-100)', fontSize: '0.88rem', color: 'var(--gray-500)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--tech)', fontWeight: 600 }}>Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  )
}
